import Head from 'next/head'
import Script from 'next/script'
import Link from 'next/link'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { contentDate, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { RequestProvider } from '../../../context/RequestContext'
import { AirconRepair } from '../../../components/index/service-category/aircon-service/AirconRepair'
import { Services } from '../../../components/index/service/Services'
import { buildAirconPriceTables } from '../../../js/aircon-cost'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { BottomButtons } from '@/index/service-category/aircon-service/BottomButtons'
import { AirconQuickLinks } from '@/index/service-category/aircon-service/AirconQuickLinks'
import { Faq } from '@/commons/Faq'
import { buildAcCompressorFaqs } from 'utils/faq'
import { buildAirconServiceGraph } from 'utils/aircon-page-schema'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-10-25'
const PUBLISHED = '2026-10-25T09:00:00+08:00'

// Created Oct 2026 for "aircon compressor repair" and its variants (~10 x 590/month),
// the one aircon intent with no owner in docs/seo/sg-aircon-pt-keyword-map.md. The WP
// posts on noisy compressors and compressor vs condenser are informational and link here.
const PATH = '/sg/aircon-servicing-singapore/aircon-compressor-repair'
const PAGE_URL = `https://www.stringssg.com${PATH}`
const HERO_IMAGE = 'aircon/2026/aircon-compressor-outdoor-unit-diagram.webp'
const H1 = 'Aircon Compressor Repair Singapore'

// Prices from the pricing profile that booking uses.
const priceTables = buildAirconPriceTables('sg')
const money = (v) => `$${v.toFixed(2)}`
const diagnosis = money(priceTables.repairDiagnosis)
const condenserChemical1 = money(priceTables.condenser[0].chemical)

const PAGE_TITLE = 'Aircon Compressor Repair Singapore | Causes & Cost'
const PAGE_DESCRIPTION = `Aircon compressor not starting, noisy or tripping the power? What usually causes it, what can be repaired, when replacement makes sense, and our ${diagnosis} diagnosis visit.`

const compressorFaqs = buildAcCompressorFaqs({ diagnosis, condenserChemical1 })

const causes = [
    {
        cause: 'Failed start capacitor',
        signs: 'Outdoor unit hums but the compressor does not start; fan may spin',
        fix: <>Replace the capacitor - see <Link href='/sg/aircon-servicing-singapore/aircon-capacitor' prefetch={false} className='text-orange-600 underline'>aircon capacitor</Link></>,
    },
    {
        cause: 'Overheating from a clogged outdoor coil',
        signs: 'Compressor runs, then cuts out after a while; outdoor unit very hot',
        fix: <>Condenser chemical cleaning ({condenserChemical1} for one) - see <Link href='/sg/aircon-servicing-singapore/aircon-condenser-cleaning' prefetch={false} className='text-orange-600 underline'>condenser cleaning</Link></>,
    },
    {
        cause: 'Low refrigerant from a leak',
        signs: 'Weak cooling, ice on the pipes, compressor running non-stop',
        fix: <>Find and fix the leak, then refill - see <Link href='/sg/aircon-servicing-singapore/aircon-gas-leakage' prefetch={false} className='text-orange-600 underline'>aircon gas leak</Link></>,
    },
    {
        cause: 'Electrical fault: contactor, wiring or control board',
        signs: 'Compressor clicks on and off, will not engage, or an error code shows',
        fix: <>Replace the faulty part - see <Link href='/sg/aircon-servicing-singapore/ac-printed-circuit-board-pcb' prefetch={false} className='text-orange-600 underline'>aircon PCB repair</Link></>,
    },
    {
        cause: 'Seized or burnt-out compressor',
        signs: 'Trips the breaker on start, loud knocking, or no response at all',
        fix: 'Compressor replacement, or replacing the unit if it is old',
    },
]

export async function getStaticProps() {
    const { month, year } = contentDate(CONTENT_REVIEWED)
    const modifiedDate = reviewedIso(CONTENT_REVIEWED)
    const airconBlogs = blogsMaster['hvac-services'].blogArray
    const wpPosts = getWpHvacServices().length > 0 ? getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services")
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ? getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services")
    setWpNewHvacServices(wpNewPosts)

    const countryName = "Singapore"
    const allRequestCategorySettings = await getRequestJSON(countryName)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    return {
        props: {
            requestCategorySettings,
            modifiedDate,
            month,
            year,
            airconBlogs,
            countryName,
            wpNewPosts,
            wpPosts,
        },
        revalidate: 259200, // In seconds
    }
}

export default function AirconCompressorRepair(props) {
    const router = useRouter()
    const { modifiedDate, airconBlogs, requestCategorySettings, wpPosts, wpNewPosts, countryName } = props
    const requestType = 'Aircon Services'
    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)

    return (
        <>
            <Head>
                <title>{PAGE_TITLE}</title>
                <meta charSet="utf-8"/>
                <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="description" content={PAGE_DESCRIPTION}/>
                <meta name="author" content="StringsSG"/>
                <meta property="og:locale" content="en_SG" />
                <meta property="og:url" content={PAGE_URL} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={PAGE_TITLE}/>
                <meta property="og:description" content={PAGE_DESCRIPTION} />
                <meta property="og:site_name" content="StringsSG" />
                <meta property="article:published_time" content={PUBLISHED} />
                <meta property="article:modified_time" content={modifiedDate} />
                <meta property="og:image" content={`https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/${HERO_IMAGE}`} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@stringssg"/>
                <meta name="twitter:title" content={PAGE_TITLE}/>
                <meta name="twitter:description" content={PAGE_DESCRIPTION}/>
                <link rel="icon" href="/favicon.ico" />
                <link rel="canonical" href={PAGE_URL}/>
                <script type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(buildAirconServiceGraph({
                        path: PATH,
                        pageName: PAGE_TITLE,
                        description: PAGE_DESCRIPTION,
                        serviceName: 'Aircon Compressor Repair Singapore',
                        serviceType: 'Aircon Repair',
                        image: `https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/${HERO_IMAGE}`,
                        datePublished: PUBLISHED,
                        modifiedDate,
                        breadcrumbName: 'Aircon Compressor Repair',
                    })) }}
                />
            </Head>
            <div>
                <RequestProvider>
                    <section id="aircon-compressor-repair" className='relative'>
                        <AirconRepair
                            setRequestForm={setRequestForm}
                            setIsQuickBook={setIsQuickBook}
                            title={H1}
                            subtitle={`Diagnosis visit ${diagnosis}, transport included. You get a quote before any repair starts.`}
                            problem="Aircon compressor not working"
                            heroImage={HERO_IMAGE}
                        />
                    </section>
                    {!requestForm &&
                        <BottomButtons countryName={countryName}/>
                    }
                    {requestForm &&
                        <Services
                            requestType={requestType}
                            requestCategorySettings={requestCategorySettings}
                            setRequestForm={setRequestForm}
                            isQuickBook={isQuickBook}
                            isCustomBook={false}
                            asSheet
                        />
                    }
                </RequestProvider>

                <div className='max-w-5xl mx-auto py-6 md:py-10 px-4 space-y-8 text-gray-700 leading-relaxed'>
                    <div className='rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2'>
                        <p className='font-semibold text-gray-900'>Short answer: it is often not the compressor.</p>
                        <p>
                            The compressor sits in the outdoor unit and pumps refrigerant around the system. When it will not start, keeps cutting
                            out or makes a racket, the cause is usually something around it: a failed start capacitor, an overheated unit with a
                            clogged coil, low gas from a leak, or an electrical part. Those are much cheaper to fix than the compressor itself, so
                            have them tested first. A StringsSG repair diagnosis visit is {diagnosis}, transport included.
                        </p>
                    </div>

                    <section className='space-y-3'>
                        <h2 className='text-2xl font-bold text-gray-900'>Signs Your Aircon Compressor Has a Problem</h2>
                        <ul className='list-disc pl-5 space-y-1'>
                            <li>The indoor unit blows air, but it is room temperature.</li>
                            <li>The outdoor fan spins but the compressor does not kick in, or the unit just hums.</li>
                            <li>The compressor clicks on and off every few minutes.</li>
                            <li>Loud knocking, rattling or grinding from the outdoor unit.</li>
                            <li>The breaker trips when the aircon starts.</li>
                            <li>An error code on the indoor unit; see the codes for <Link href='/sg/aircon-servicing-singapore/daikin' prefetch={false} className='text-orange-600 underline'>Daikin</Link>, <Link href='/sg/aircon-servicing-singapore/panasonic' prefetch={false} className='text-orange-600 underline'>Panasonic</Link>, <Link href='/sg/aircon-servicing-singapore/lg-air-conditioner' prefetch={false} className='text-orange-600 underline'>LG</Link> and <Link href='/sg/aircon-servicing-singapore/samsung' prefetch={false} className='text-orange-600 underline'>Samsung</Link>.</li>
                        </ul>
                    </section>

                    <section className='space-y-3'>
                        <h2 className='text-2xl font-bold text-gray-900'>What Usually Causes It, and What Fixes It</h2>
                        <div className='overflow-x-auto rounded-xl border border-gray-200'>
                            <table className='w-full text-left text-sm'>
                                <caption className='sr-only'>Causes of aircon compressor problems in Singapore and their fixes</caption>
                                <thead className='bg-gray-50 text-gray-700'>
                                    <tr>
                                        <th scope='col' className='p-3 font-semibold'>Cause</th>
                                        <th scope='col' className='p-3 font-semibold'>What you notice</th>
                                        <th scope='col' className='p-3 font-semibold'>Fix</th>
                                    </tr>
                                </thead>
                                <tbody className='divide-y divide-gray-200'>
                                    {causes.map((row) => (
                                        <tr key={row.cause}>
                                            <td className='p-3 font-semibold text-gray-900'>{row.cause}</td>
                                            <td className='p-3'>{row.signs}</td>
                                            <td className='p-3'>{row.fix}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section className='space-y-3'>
                        <h2 className='text-2xl font-bold text-gray-900'>Before You Call a Technician</h2>
                        <ol className='list-decimal pl-5 space-y-1'>
                            <li>Check the breaker and the isolator switch next to the outdoor unit are on.</li>
                            <li>After switching off, wait 3 to 5 minutes before restarting; most units delay the compressor on purpose to protect it.</li>
                            <li>Clear anything stacked against the outdoor unit so air can flow through the coil.</li>
                            <li>Clean the indoor filters; a choked filter can make the system freeze up and stop.</li>
                            <li>Note any error code and the model number from the label before you book.</li>
                        </ol>
                    </section>

                    <section className='space-y-3'>
                        <h2 className='text-2xl font-bold text-gray-900'>Repair or Replace?</h2>
                        <p>
                            The parts around the compressor are routine repairs. A compressor that has seized or burnt out is replaced, and on an
                            older unit that can cost a large share of a new aircon. As a rule of thumb, if the unit is more than eight to ten years
                            old, still runs on R22, or has needed several repairs already, put the money towards a new, more efficient unit instead;
                            see <Link href='/sg/aircon-servicing-singapore/aircon-installation' prefetch={false} className='text-orange-600 underline'>aircon supply and installation</Link>.
                            Check your warranty card first, because many brands cover the compressor for longer than the rest of the aircon.
                        </p>
                    </section>

                    <section className='space-y-3'>
                        <h2 className='text-2xl font-bold text-gray-900'>How StringsSG Handles a Compressor Fault</h2>
                        <p>
                            Book an aircon repair and describe what the unit is doing. The technician tests the capacitor, contactor, gas pressure
                            and the compressor&apos;s current draw to find the actual fault, then quotes the repair before any work starts. Repairs
                            carry a 30-day workmanship warranty. For background, read{' '}
                            <Link href='/sg/aircon-servicing-singapore/compressor-vs-condenser-is-there-any-important-difference' prefetch={false} className='text-orange-600 underline'>compressor vs condenser</Link>{' '}
                            or go back to all <Link href='/sg/aircon-servicing-singapore/aircon-repair' prefetch={false} className='text-orange-600 underline'>aircon repair</Link> problems.
                        </p>
                    </section>
                </div>

                <section className='max-w-5xl mx-auto px-4 pb-10'>
                    <Faq questionsAnswers={compressorFaqs} title='Aircon Compressor Repair FAQs' />
                </section>
                <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
                <AirconQuickLinks/>
            </div>
            <Script
                id="google-tagmanager-new"
                src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
                strategy="lazyOnload"
                onLoad={() => {
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', 'G-QD1HGGHDYJ');
                }}
            />
        </>
    )
}
